import {
  validateSegmentCount,
  validateSegmentModel,
  type CustomerValueScaleValidationError,
  type CustomerValueSegmentDefinition
} from "./scoringScaleValidator";

export type CustomerValueSegmentLifecycleStatus = "draft" | "ready_for_confirmation" | "confirmed" | "superseded";

export type CustomerValueSegmentErrorCode =
  | "segment_definition_not_found"
  | "segment_definition_not_editable"
  | "segment_draft_conflict"
  | "segment_model_invalid"
  | "invalid_segment_count"
  | "segment_gap"
  | "segment_overlap"
  | "invalid_best_worst_rank"
  | "duplicate_segment_rank"
  | "model_version_conflict"
  | "access_denied";

export interface CustomerValueSegmentContract {
  id: string;
  modelId: string;
  modelVersion: number;
  name: string;
  description: string;
  rank: number;
  minScore: number;
  maxScore: number;
  minInclusive: boolean;
  maxInclusive: boolean;
  isBest: boolean;
  isWorst: boolean;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SegmentDefinitionValidationResult {
  ok: boolean;
  errors: Array<Omit<CustomerValueScaleValidationError, "code"> & { code: CustomerValueSegmentErrorCode | CustomerValueScaleValidationError["code"] }>;
  normalizedSegments?: CustomerValueSegmentContract[];
}

export function toValidatorSegment(segment: CustomerValueSegmentContract): CustomerValueSegmentDefinition {
  return {
    id: segment.id,
    name: segment.name,
    description: segment.description,
    rank: segment.rank,
    minScore: segment.minScore,
    maxScore: segment.maxScore,
    minInclusive: segment.minInclusive,
    maxInclusive: segment.maxInclusive,
    isBest: segment.isBest,
    isWorst: segment.isWorst,
    active: segment.active
  };
}

function normalizedSegment(segment: CustomerValueSegmentContract): CustomerValueSegmentContract {
  return {
    ...segment,
    name: segment.name.trim(),
    description: segment.description.trim(),
    rank: Number(segment.rank),
    minScore: Number(segment.minScore),
    maxScore: Number(segment.maxScore),
    minInclusive: Boolean(segment.minInclusive),
    maxInclusive: Boolean(segment.maxInclusive),
    isBest: Boolean(segment.isBest),
    isWorst: Boolean(segment.isWorst),
    active: Boolean(segment.active)
  };
}

export function deriveCustomerValueSegmentOrder(
  segments: readonly CustomerValueSegmentContract[]
): CustomerValueSegmentContract[] {
  const ordered = segments
    .filter((segment) => segment.active)
    .map(normalizedSegment)
    .sort((left, right) => right.maxScore - left.maxScore || right.minScore - left.minScore || left.name.localeCompare(right.name));

  return ordered.map((segment, index) => ({
    ...segment,
    rank: index + 1,
    isBest: index === 0,
    isWorst: index === ordered.length - 1
  }));
}

export function validateCustomerValueSegmentDefinition(
  segments: readonly CustomerValueSegmentContract[]
): SegmentDefinitionValidationResult {
  const activeSegments = deriveCustomerValueSegmentOrder(segments);
  const segmentCount = validateSegmentCount(activeSegments.length);
  if (!segmentCount.ok) {
    return {
      ok: false,
      errors: segmentCount.errors.map((entry) => ({ ...entry, code: "invalid_segment_count" as const }))
    };
  }
  const validation = validateSegmentModel({
    minimumScore: 0,
    maximumScore: 100,
    segments: activeSegments.map(toValidatorSegment)
  });
  if (!validation.ok) {
    return { ok: false, errors: validation.errors };
  }
  return {
    ok: true,
    errors: [],
    normalizedSegments: activeSegments.sort((left, right) => left.rank - right.rank)
  };
}
